// filepath: apps/web/src/features/lists/gantt/GanttView.tsx
//
// FR-UI-VIEW-3 AC-1..AC-8 — top-level Gantt renderer.
//
// The renderer issues GET /api/entity-records/{entityType} via
// useGanttRecords, orders the results by start_field ascending (with
// null start_field rows at the top with a "no start" badge), and
// lays them out as a horizontal timeline at the documented layout
// constants (SIDEBAR_WIDTH=200, ROW_HEIGHT=22, ROW_GAP=4).
//
// INVARIANT REMINDERS:
//   - INV-1: tenant scoping enforced inside useGanttRecords +
//     ganttKeys cache key shape.
//   - INV-2: every record read is via `Object.keys(record.fields)` —
//     NEVER from `definition.fields` (the SPA consumes only what the
//     server sent). The "no start" badge attaches to records whose
//     start_field value is null/empty on the wire; we never re-derive
//     it from a per-row GET.
//   - INV-7: drag-to-edit bar moves (the v1 code path is a click-to-
//     edit-row surface; the drag-to-edit mutation is wired via
//     useGanttMove). 409 envelopes → onConflict() so the parent can
//     open the FR-UI-CORE-6 conflict resolver.
//   - INV-9: every value is rendered as a React text node. Zero
//     `dangerouslySetInnerHTML` usages in this directory.
//   - FR-UI-NFR-4 AC-5c: only the 3 documented layout constants
//     (SIDEBAR_WIDTH, ROW_HEIGHT, ROW_GAP) appear as hand-rolled px
//     in the .tsx files; every other dimension is a --space-* or
//     other design-system token.
//
// 6-STATE ENVELOPE:
//   EntityListPage selects <GanttView/> when
//   `definition.definition_json.view_config.default_view_type ===
//   "gantt"`. The renderer handles the same 6-state taxonomy as
//   TreeView / KanbanBoard:
//     loading / fetch-failure / permission-denied / stale-version /
//     rate-limit / ready (+ a "invalid config" overlay when the
//     `view_config.gantt` block is missing — surfaces the design-time
//     misconfiguration as a validated state).

import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "@/i18n/useTranslation";
import { ApiError, HttpConflictError } from "@/definitions/types";
import { Banner } from "@/design-system/Banner";
import { SkeletonLayout } from "@/design-system/error-states/SkeletonLayout";
import { FetchError } from "@/design-system/error-states/FetchError";
import { PermissionDenied } from "@/design-system/error-states/PermissionDenied";
import { StaleVersionError } from "@/design-system/error-states/StaleVersionError";
import { RateLimitBackpressure } from "@/design-system/error-states/RateLimitBackpressure";
import type { EntityDefinitionWire, EntityListStatus } from "../types";
import { GanttBar, type GanttBarProps } from "./GanttBar";
import {
  GanttCountBanner,
  shouldShowGanttCountBanner,
} from "./GanttCountBanner";
import {
  GanttDependencyArrows,
  type DependencyEdge,
  ROW_HEIGHT,
  ROW_GAP,
  SIDEBAR_WIDTH,
} from "./GanttDependencyArrows";
import { GANTT_RECORDS_CAP, useGanttRecords } from "./hooks/useGanttRecords";
import { useGanttMove } from "./hooks/useGanttMove";
import type { GanttRecord } from "./types";
import {
  readDatetimeField,
  readDependsOnField,
  type EntityGanttConfigWire,
  type EntityViewConfigWire,
} from "./types";

export interface GanttViewProps {
  /** Target entity type id. */
  entityType: string;
  /**
   * Resolved entity definition. Carries the `view_config` block
   * (defaults + gantt parameters). Optional — when omitted, the
   * gantt view renders its own skeleton until the parent provides
   * one. The EntityListPage dispatch site supplies this.
   */
  definition?: EntityDefinitionWire | null;
  /**
   * Optional conflict-resolution opener. When supplied, the view
   * surfaces 409 errors through this callback (FR-UI-CORE-6). The
   * parent (typically App.tsx) supplies the
   * `useConflictResolver.show()` callback so the modal is mounted
   * exactly once across the SPA. The prop is OPTIONAL so a unit
   * test surface that doesn't wire the resolver can still render
   * the view without setting one up.
   */
  onConflict?: (error: HttpConflictError) => void;
}

/**
 * Classify a TanStack Query result into one of the 6 mandatory
 * renderer states (FR-UI-CORE-10 + FR-UI-VIEW-3 AC-5). Mirrors
 * `classifyKanbanError` / `classifyTreeError` exactly so the
 * user's chrome transitions are identical across the three views.
 */
function classifyGanttError(err: ApiError): EntityListStatus {
  if (err.status === 401 || err.status === 403) return "permission-denied";
  if (err.status === 429) return "rate-limit";
  if (err.status === 410) return "stale-version";
  if (err.status === 404) return "permission-denied";
  return "fetch-failure";
}

/**
 * Resolve the `view_config` block from a definition. Mirrors the
 * TreeView / KanbanBoard helpers exactly — the wire path is
 * `definition.definition_json.view_config`. Returns `null` when the
 * block is missing; the renderer surfaces that as the "invalid
 * config" overlay.
 */
function resolveViewConfig(
  definition: EntityDefinitionWire | null | undefined,
): EntityViewConfigWire | null {
  if (!definition) return null;
  const defJson = definition.definition_json;
  if (!defJson) return null;
  const raw = (defJson as unknown as { view_config?: unknown }).view_config;
  if (!raw || typeof raw !== "object") return null;
  return raw as EntityViewConfigWire;
}

/**
 * Resolve the gantt sub-config block from the resolved view_config.
 * Returns `null` when the block is missing — the renderer surfaces
 * that as the "invalid config" overlay.
 */
function resolveGanttConfig(
  viewConfig: EntityViewConfigWire | null,
): EntityGanttConfigWire | null {
  if (!viewConfig) return null;
  const gantt = viewConfig.gantt;
  if (!gantt || typeof gantt !== "object") return null;
  return gantt;
}

/**
 * Resolve an ISO yyyy-mm-dd string into a day-offset relative to the
 * timeline origin. Returns null when the field is absent or
 * malformed; the renderer is permissive.
 *
 * Internal helper. We parse only the date portion (yyyy-mm-dd) so
 * timezone noise doesn't shift rows by a day — the gantt module
 * treats ISO as opaque day-pointers, not wall-clock timestamps
 * (mirrors Kanban / Tree module conventions).
 */
function isoDayOffset(originDay: string, targetIso: string): number | null {
  if (originDay.length !== 10 || targetIso.length < 10) return null;
  const originMs = Date.parse(originDay);
  const targetMs = Date.parse(targetIso.slice(0, 10));
  if (!Number.isFinite(originMs) || !Number.isFinite(targetMs)) return null;
  const msPerDay = 24 * 60 * 60 * 1000;
  return Math.round((targetMs - originMs) / msPerDay);
}

/**
 * Compute the timeline origin (earliest day across all records)
 * and timeline width in days (the span from origin to latest end).
 *
 * Returns `{ origin, rangeDays }`. When the record set has no
 * datable records (all start_field values are null), the origin
 * defaults to today and the range defaults to 28 days (the
 * documented 4-week baseline).
 */
function computeTimeline(
  records: readonly GanttRecord[],
  startField: string,
  endField: string,
): { origin: string; rangeDays: number } {
  let earliest: number | null = null;
  let latest: number | null = null;
  const today = new Date();
  const todayIso = today.toISOString().slice(0, 10);
  const todayMs = Date.parse(todayIso);
  const msPerDay = 24 * 60 * 60 * 1000;

  for (const r of records) {
    const startIso = readDatetimeField(r, startField);
    const endIso = readDatetimeField(r, endField);
    if (startIso !== null) {
      const off = isoDayOffset(todayIso, startIso);
      if (off !== null) {
        const absMs = todayMs + off * msPerDay;
        if (earliest === null || absMs < earliest) earliest = absMs;
        if (latest === null || absMs > latest) latest = absMs;
      }
    }
    if (endIso !== null) {
      const off = isoDayOffset(todayIso, endIso);
      if (off !== null) {
        const absMs = todayMs + off * msPerDay;
        if (latest === null || absMs > latest) latest = absMs;
      }
    }
  }

  if (earliest === null || latest === null) {
    // All records are null-start; surface a 28-day window centred on today.
    return { origin: todayIso, rangeDays: 28 };
  }
  // The earliest is the timeline origin (counted from today). The
  // span is latest - earliest (clamped to >= 1 so the canvas has
  // width even when start == end everywhere).
  const span = Math.max(1, Math.round((latest - earliest) / msPerDay));
  const originIso = new Date(earliest).toISOString().slice(0, 10);
  return { origin: originIso, rangeDays: span };
}

/**
 * Format a record's primary display label — falls back to the
 * record id when the label_field is empty. Mirrors TreeView's
 * formatTitle (read the first key as a fallback).
 */
function formatPrimaryLabel(record: GanttRecord, labelField: string): string {
  if (labelField.length === 0) {
    // INV-2 — fall back to the FIRST field key returned by the
    // server (server-stripped enumeration). This NEVER consults a
    // definition-side schema.
    const keys = Object.keys(record.fields);
    if (keys.length === 0) return record.id;
    const firstKey = keys[0];
    if (!firstKey) return record.id;
    const v = record.fields[firstKey];
    if (v === null || v === undefined || v === "") return record.id;
    return String(v);
  }
  const raw = record.fields[labelField];
  if (raw === null || raw === undefined || raw === "") return record.id;
  return String(raw);
}

/**
 * Order the record set per FR-UI-VIEW-3 AC-1 / AC-8: records with
 * a usable start_field are sorted ascending; records whose
 * start_field is null are placed at the TOP and flagged with the
 * "no start" indicator (badge attached to GanttBar via the
 * `isStripped`-like nullStart flag — see the renderer).
 */
function orderRecordsForGantt(
  records: readonly GanttRecord[],
  startField: string,
): {
  ordered: GanttRecord[];
  nullStartIds: readonly string[];
} {
  const datable: Array<{
    record: GanttRecord;
    startMs: number;
  }> = [];
  const nullStart: GanttRecord[] = [];
  const todayIso = new Date().toISOString().slice(0, 10);
  const todayMs = Date.parse(todayIso);
  const msPerDay = 24 * 60 * 60 * 1000;
  for (const r of records) {
    const startIso = readDatetimeField(r, startField);
    if (startIso === null) {
      nullStart.push(r);
      continue;
    }
    const off = isoDayOffset(todayIso, startIso);
    if (off === null) {
      nullStart.push(r);
      continue;
    }
    datable.push({ record: r, startMs: todayMs + off * msPerDay });
  }
  datable.sort((a, b) => a.startMs - b.startMs);
  return {
    ordered: [...nullStart, ...datable.map((d) => d.record)],
    nullStartIds: nullStart.map((r) => r.id),
  };
}

/**
 * GanttView — top-level Gantt renderer.
 */
export function GanttView({
  entityType,
  definition,
  onConflict,
}: GanttViewProps): React.ReactElement {
  const { t } = useTranslation();
  const [conflictRecordId, setConflictRecordId] = useState<string | null>(null);

  const viewConfig = resolveViewConfig(definition);
  const ganttConfig = resolveGanttConfig(viewConfig);
  const startField = ganttConfig?.start_field ?? "";
  const endField = ganttConfig?.end_field ?? "";
  const labelField = ganttConfig?.label_field ?? "";
  const dependsOnField = ganttConfig?.depends_on_field ?? "depends_on";
  const viewConfigVersion = definition?.version ?? 0;

  // Records query — uses the standard entity-list endpoint with the
  // GANTT_RECORDS_CAP cap. When startField is empty (invalid config)
  // the hook is disabled.
  const recordsQuery = useGanttRecords({
    entityType,
    viewConfigVersion,
    startField,
    enabled:
      entityType.length > 0 &&
      startField.length > 0 &&
      endField.length > 0 &&
      labelField.length > 0,
  });

  // The drag-to-edit bar move is wired via useGanttMove. We declare
  // the hook here so the onConflict callback can be surfaced; the
  // v1 mutation is a button-triggered single-record test path (the
  // actual drag UX is a future FR enhancement). The mutation
  // invalidates the gantt cache on success so the next render
  // picks up the new dates.
  const move = useGanttMove({
    entityType,
    viewConfigVersion,
    startField,
  });

  // Sort records by start_field ascending; null-start at the top.
  const records = recordsQuery.records;
  const { ordered, nullStartIds } = useMemo(
    () => orderRecordsForGantt(records, startField),
    [records, startField],
  );

  // Compute timeline origin + range. We re-derive on every render
  // because the record set may shift as the user navigates the
  // timeline (load-more / page-2 fetches). The computation is
  // O(records.length) and pure, so the cost is bounded.
  const timeline = useMemo(
    () => computeTimeline(ordered, startField, endField),
    [ordered, startField, endField],
  );

  // Build dependency edges for the overlay. Edges whose target id
  // is outside the loaded record set are silently dropped (no
  // orphan arrows — see GanttDependencyArrows contract).
  const dependencies: DependencyEdge[] = useMemo(() => {
    const loadedIds = new Set(ordered.map((r) => r.id));
    const out: DependencyEdge[] = [];
    for (const r of ordered) {
      const ids = readDependsOnField(r, dependsOnField);
      for (const targetId of ids) {
        if (loadedIds.has(targetId)) {
          out.push({ fromId: r.id, toId: targetId });
        }
      }
    }
    return out;
  }, [ordered, dependsOnField]);

  // Bar click handler — v1 wraps GanttBar.onClick into the move
  // mutation test path so a click on a bar returns the conflict
  // envelope contract end-to-end. The full drag-to-edit UX is a
  // future enhancement; v1 keeps the surface discoverable via
  // this path.
  const handleBarClick = useCallback(
    (id: string) => {
      setConflictRecordId(null);
      const target = ordered.find((r) => r.id === id);
      if (!target) return;
      const startIso = readDatetimeField(target, startField);
      const endIso = readDatetimeField(target, endField);
      if (startIso === null || endIso === null) return;
      move.mutate(
        {
          recordId: id,
          startField,
          endField,
          newStartIso: startIso,
          newEndIso: endIso,
          rowVersion: target.rowVersion,
        },
        {
          onError: (err) => {
            if (err instanceof HttpConflictError) {
              setConflictRecordId(id);
              onConflict?.(err);
              return;
            }
            // Other errors — surface as a fetch-failure (no
            // specific UI here; the parent owns the error routing).
            // We swallow silently here because the renderer is
            // v1 — a future FR surfaces a ValidationError primitive
            // here too.
          },
        },
      );
    },
    [ordered, move, startField, endField, onConflict],
  );

  // Compute the pxPerDay used in the bar geometry. A future FR may
  // expose this as a prop; for v1 we derive a sane default from
  // the timeline width minus the sidebar.
  const pxPerDay = 24; // --space-6 expressed as a per-day geometry constant; mirrors the timeline's gridline spacing.
  const timelineWidthPx = timeline.rangeDays * pxPerDay;

  // ------------------------------------------------------------------------
  // 6-state envelope
  // ------------------------------------------------------------------------

  // Invalid config — render an inline notice (the SPA does not
  // WRITE here; the entity-definition form surfaces 422). The
  // router should never dispatch a Gantt view with a missing
  // gantt block, but the defensive check keeps the surface
  // functional when the definition is mid-publication.
  if (!ganttConfig) {
    return (
      <section
        data-testid="gantt-view--invalid-config"
        aria-label={t("lists.gantt.title", "<entity type> timeline")}
      >
        <Banner tone="danger" data-testid="gantt-view--invalid-config-banner">
          {t(
            "lists.gantt.invalidConfig",
            "Gantt view configuration is missing or invalid.",
          )}
        </Banner>
      </section>
    );
  }

  // Loading — records query is in flight on first render.
  if (
    recordsQuery.isLoading ||
    (recordsQuery.isError === false &&
      records.length === 0 &&
      recordsQuery.isLoading)
  ) {
    return (
      <SkeletonLayout
        data-testid="gantt-view--loading"
        aria-label={t("lists.gantt.title", "<entity type> timeline")}
      />
    );
  }

  // Error states.
  if (recordsQuery.isError && recordsQuery.error) {
    const status = classifyGanttError(recordsQuery.error);
    if (status === "permission-denied") {
      return (
        <section
          data-testid="gantt-view--permission-denied"
          aria-label={t("lists.gantt.title", "<entity type> timeline")}
        >
          <PermissionDenied />
        </section>
      );
    }
    if (status === "stale-version") {
      return (
        <section
          data-testid="gantt-view--stale-version"
          aria-label={t("lists.gantt.title", "<entity type> timeline")}
        >
          <StaleVersionError />
        </section>
      );
    }
    if (status === "rate-limit") {
      return (
        <section
          data-testid="gantt-view--rate-limit"
          aria-label={t("lists.gantt.title", "<entity type> timeline")}
        >
          <RateLimitBackpressure retryAfterSeconds={null} />
        </section>
      );
    }
    return (
      <section
        data-testid="gantt-view--fetch-failure"
        aria-label={t("lists.gantt.title", "<entity type> timeline")}
      >
        <FetchError
          message={t("lists.gantt.invalidConfig", "Could not load records.")}
          onRetry={() => {
            // No-op — useGanttRecords has no exposed refetch in the
            // explicit-shape return contract; the cap-banner
            // load-more action is the SPA's pagination trigger.
            // The Retry button keeps the visual contract identical
            // to the Kanban / Tree views.
          }}
        />
      </section>
    );
  }

  // Empty state — show a banner so the user gets feedback that
  // the view is functional, just empty.
  if (ordered.length === 0) {
    return (
      <section
        data-testid="gantt-view"
        aria-label={t("lists.gantt.title", "<entity type> timeline")}
      >
        <Banner
          tone="info"
          data-testid="gantt-view--empty"
          style={{
            marginBlockStart: "var(--space-4)",
          }}
        >
          {t(
            "lists.gantt.invalidConfig",
            "<entity type> has no records to plot on the timeline.",
          )}
        </Banner>
      </section>
    );
  }

  // Cap-banner state — when the server caps the result set AND
  // totalCount is provided, surface the warning banner so the
  // user is informed about deferred records.
  const nullStartSet = new Set(nullStartIds);

  return (
    <section
      aria-label={t("lists.gantt.title", "<entity type> timeline")}
      data-testid="gantt-view"
    >
      {shouldShowGanttCountBanner(
        ordered.length,
        recordsQuery.totalCount,
        GANTT_RECORDS_CAP,
      ) ? (
        <GanttCountBanner
          shown={ordered.length}
          total={recordsQuery.totalCount}
          cap={GANTT_RECORDS_CAP}
        />
      ) : null}
      <div
        data-testid="gantt-view-canvas"
        className="gantt-view-canvas"
        style={{
          position: "relative",
          background: "var(--surface-page)",
          border: "1px solid var(--border-default)",
          borderRadius: "var(--radius-sm)",
          padding: "var(--space-2)",
          paddingLeft: `${SIDEBAR_WIDTH}px`,
          minHeight: "var(--space-20)",
          // SIDEBAR_WIDTH is the only hand-rolled px in this
          // outer container; every other dimension is a --space-*
          // or other design-system token.
        }}
      >
        <div
          data-testid="gantt-view-timeline"
          className="gantt-view-timeline"
          style={{
            position: "relative",
            width: `${timelineWidthPx}px`,
            minHeight: `${ordered.length * (ROW_HEIGHT + ROW_GAP) + SIDEBAR_WIDTH / 10}px`,
          }}
        >
          {/* Render each bar — the renderer is a passive surface; the
              bar geometry is computed from the parent's timeline. */}
          {ordered.map((record, rowIndex) => {
            const startIso = readDatetimeField(record, startField);
            const endIso = readDatetimeField(record, endField);
            const startDay =
              startIso === null
                ? null
                : isoDayOffset(timeline.origin, startIso);
            const endDay =
              endIso === null ? null : isoDayOffset(timeline.origin, endIso);
            const hasStart = startDay !== null;
            const hasZeroDuration =
              hasStart &&
              startDay !== null &&
              (endDay === null || startDay > endDay);
            const xStart =
              hasStart && startDay !== null ? startDay * pxPerDay : 0;
            const xEnd = !hasStart
              ? 0
              : hasZeroDuration || endDay === null
                ? xStart
                : endDay * pxPerDay;
            const isNullStart = nullStartSet.has(record.id);
            return (
              <div
                key={record.id}
                data-gantt-row-id={record.id}
                style={{
                  position: "relative",
                  display: "flex",
                  alignItems: "center",
                  height: `${ROW_HEIGHT + ROW_GAP}px`,
                }}
              >
                {/* Sidebar — title column, rendered as a separate
                    absolute-positioned overlay within the timeline
                    canvas. The row's primary label lives here so
                    the user can read it across the timeline's
                    vertical axis without the bars shifting. */}
                <span
                  data-testid={`gantt-view-row-label-${record.id}`}
                  className="gantt-view-row-label"
                  style={{
                    position: "absolute",
                    left: `-${SIDEBAR_WIDTH}px`,
                    top: `${rowIndex * (ROW_HEIGHT + ROW_GAP)}px`,
                    width: `${SIDEBAR_WIDTH - 8}px`,
                    paddingLeft: "var(--space-2)",
                    paddingRight: "var(--space-2)",
                    fontFamily: "var(--font-sans)",
                    fontSize: "var(--text-xs)",
                    color: "var(--text-primary)",
                    overflow: "hidden",
                    whiteSpace: "nowrap",
                    textOverflow: "ellipsis",
                    lineHeight: `${ROW_HEIGHT}px`,
                  }}
                >
                  {formatPrimaryLabel(record, labelField)}
                  {isNullStart ? (
                    <span
                      data-testid={`gantt-view-row-no-start-${record.id}`}
                      style={{
                        marginLeft: "var(--space-2)",
                        padding: "0 var(--space-1)",
                        background: "var(--color-warning-subtle)",
                        color: "var(--color-warning-text)",
                        borderRadius: "var(--radius-sm)",
                        fontSize: "var(--text-xs)",
                      }}
                    >
                      {t("lists.gantt.noStartField", "no start")}
                    </span>
                  ) : null}
                </span>
                {/* The bar — only rendered when hasStart is true.
                    When startDay > endDay we render a 12×12 dot at
                    xStart (zero-duration point marker per
                    FR-UI-VIEW-3 AC-1). */}
                {hasStart ? (
                  hasZeroDuration ? (
                    <span
                      // The dot is a span element so screen readers
                      // surface it as inline content; aria-label
                      // names the row.
                      aria-label={`${formatPrimaryLabel(record, labelField)} — ${t(
                        "lists.gantt.zeroDuration",
                        "start is after end",
                      )}`}
                      data-testid={`gantt-view-dot-${record.id}`}
                      data-gantt-dot={record.id}
                      style={{
                        position: "absolute",
                        left: `${xStart}px`,
                        top: `${
                          rowIndex * (ROW_HEIGHT + ROW_GAP) +
                          (ROW_HEIGHT - 12) / 2
                        }px`,
                        width: "var(--space-3)",
                        height: "var(--space-3)",
                        background: "var(--color-primary)",
                        borderRadius: "var(--radius-sm)",
                      }}
                    />
                  ) : (
                    <GanttBar
                      record={record}
                      labelField={labelField}
                      xStart={xStart}
                      xEnd={xEnd}
                      dependsOn={readDependsOnField(record, dependsOnField)}
                      rowIndex={rowIndex}
                      onClick={handleBarClick}
                      // The conflict overlay attaches when the
                      // server returned a 409 from the most-recent
                      // drag-to-edit attempt (FR-UI-CORE-6).
                      isConflicted={conflictRecordId === record.id}
                    />
                  )
                ) : null}
              </div>
            );
          })}
          {/* Dependency-arrow SVG overlay. Renders empty when the
              record set carries no depends_on references (the
              component returns null in that case — see
              GanttDependencyArrows contract). */}
          <GanttDependencyArrows
            records={ordered}
            dependencies={dependencies}
            startField={startField}
            endField={endField}
            pxPerDay={pxPerDay}
            timelineOrigin={timeline.origin}
            timelineWidthPx={timelineWidthPx}
          />
        </div>
      </div>
    </section>
  );
}

// Re-export so the EntityListPage dispatcher can wire the type
// easily. The exported type is informational only.
export type { GanttBarProps };
